// src/components/network/interactions/CommentSection.js
import React, { useState, useEffect } from 'react';
import { Send, User, Building } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { db } from '../../../firebase/config';
import { 
  collection, addDoc, query, where, orderBy, 
  getDocs, serverTimestamp, doc, getDoc, updateDoc, increment 
} from 'firebase/firestore';
import { formatDistanceToNow } from 'date-fns';

const CommentSection = ({ postId, isAuthenticated = false }) => {
  const { currentUser } = useAuth();
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Fetch comments
  useEffect(() => {
    const fetchComments = async () => {
      try {
        setLoading(true);
        
        const commentsQuery = query(
          collection(db, 'comments'),
          where('postId', '==', postId),
          orderBy('createdAt', 'asc')
        );
        
        const commentsSnapshot = await getDocs(commentsQuery);
        
        // Process comments and get author info
        const commentsPromises = commentsSnapshot.docs.map(async (commentDoc) => {
          const commentData = commentDoc.data();
          
          // Get author data
          let authorData = { id: commentData.authorId, name: 'Unknown User' };
          
          try {
            // First check if it's a business
            const businessRef = doc(db, 'businesses', commentData.authorId);
            const businessSnap = await getDoc(businessRef);
            
            if (businessSnap.exists()) {
              authorData = {
                id: businessSnap.id,
                ...businessSnap.data(),
                type: 'business'
              };
            } else {
              // If not a business, check users collection
              const userRef = doc(db, 'users', commentData.authorId);
              const userSnap = await getDoc(userRef);
              
              if (userSnap.exists()) {
                const userData = userSnap.data();
                authorData = {
                  id: userSnap.id,
                  name: userData.displayName || userData.email,
                  email: userData.email,
                  type: 'user'
                };
              }
            }
          } catch (err) {
            console.error('Error fetching comment author:', err);
          }
          
          return {
            id: commentDoc.id,
            ...commentData,
            createdAt: commentData.createdAt?.toDate() || new Date(),
            author: authorData
          };
        });
        
        const processedComments = await Promise.all(commentsPromises);
        setComments(processedComments);
        
      } catch (error) {
        console.error('Error fetching comments:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchComments();
  }, [postId]);
  
  // Add a comment
  const handleAddComment = async (e) => {
    e.preventDefault();
    
    if (!isAuthenticated || !currentUser) {
      window.location.href = '/login?redirect=' + encodeURIComponent(window.location.pathname);
      return;
    }
    
    if (!commentText.trim()) return;
    
    try {
      setSubmitting(true);
      
      // Add comment to Firestore
      const commentData = {
        postId,
        authorId: currentUser.uid,
        content: commentText.trim(),
        createdAt: serverTimestamp()
      };
      
      const docRef = await addDoc(collection(db, 'comments'), commentData);
      
      // Update comment count on the post
      const postRef = doc(db, 'posts', postId);
      await updateDoc(postRef, {
        commentCount: increment(1)
      });
      
      // Add to local state without waiting for a refetch
      const newComment = {
        id: docRef.id,
        ...commentData,
        createdAt: new Date(),
        author: {
          id: currentUser.uid,
          name: currentUser.displayName || currentUser.email,
          email: currentUser.email,
          type: 'user'
        }
      };
      
      setComments(prev => [...prev, newComment]);
      setCommentText('');
      
    } catch (error) {
      console.error('Error adding comment:', error);
    } finally {
      setSubmitting(false);
    }
  };
  
  // Format date
  const formatDate = (date) => {
    if (!date) return '';
    try {
      return formatDistanceToNow(new Date(date), { addSuffix: true });
    } catch (e) {
      return '';
    }
  };
  
  return (
    <div className="p-4 border-t border-gray-100">
      <h3 className="font-medium text-gray-900 mb-4">Comments</h3>
      
      {/* Comments list */}
      <div className="space-y-4 mb-4">
        {loading ? (
          <div className="animate-pulse space-y-4">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="flex">
                <div className="h-8 w-8 bg-gray-200 rounded-full mr-3"></div>
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                </div>
              </div>
            ))}
          </div>
        ) : comments.length === 0 ? (
          <p className="text-gray-500 text-center py-4">
            No comments yet. Be the first to share your thoughts!
          </p>
        ) : (
          comments.map(comment => (
            <div key={comment.id} className="flex">
              <div className="w-8 h-8 rounded-full bg-gray-100 mr-3 flex-shrink-0 overflow-hidden">
                {comment.author.type === 'business' && comment.author.businessLogoUrl ? (
                  <img 
                    src={comment.author.businessLogoUrl} 
                    alt={comment.author.name} 
                    className="w-full h-full object-cover"
                  />
                ) : comment.author.type === 'business' ? (
                  <div className="w-full h-full flex items-center justify-center bg-purple-100 text-[#190192]">
                    <Building size={16} />
                  </div>
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gray-200 text-gray-600">
                    <User size={16} />
                  </div>
                )}
              </div>
              <div className="flex-1">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-gray-900">{comment.author.name}</span>
                    <span className="text-xs text-gray-500">{formatDate(comment.createdAt)}</span>
                  </div>
                  <p className="text-gray-700">{comment.content}</p>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
      
      {/* Comment form */}
      {isAuthenticated ? (
        <form onSubmit={handleAddComment} className="flex">
          <input
            type="text"
            className="flex-1 border border-gray-200 rounded-l-lg px-3 py-2 focus:outline-none focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
            placeholder="Add a comment..."
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            disabled={submitting}
          />
          <button
            type="submit"
            disabled={submitting || !commentText.trim()}
            className="px-4 py-2 bg-[#190192] text-white rounded-r-lg hover:bg-[#2C0DB5] disabled:bg-gray-400"
          >
            {submitting ? (
              <div className="animate-spin h-5 w-5 border-2 border-white rounded-full border-t-transparent"></div>
            ) : (
              <Send size={18} />
            )}
          </button>
        </form>
      ) : (
        <div className="text-center py-2 border border-gray-200 rounded-lg bg-gray-50">
          <p className="text-gray-600 text-sm">
            <a href="/login" className="text-[#190192] hover:underline">Sign in</a> to leave a comment
          </p>
        </div>
      )}
    </div>
  );
};

export default CommentSection;